'use client';

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, Edit2Icon, X } from 'lucide-react';
import { Textarea } from './textarea';
import { TagInput } from './tag-input';

export function RelevantPromptDialog({
	prompt,
	onEdit,
	onRemove,
	triggerButton
}: {
	prompt: RelevantPrompts;
	onEdit: (prompt: RelevantPrompts) => void;
	onRemove?: (prompt: RelevantPrompts) => void;
	triggerButton?: React.ReactNode;
}) {
	const [isEditing, setIsEditing] = useState(false);
	const [editedPrompt, setEditedPrompt] = useState(prompt);
	const [isLoading, setIsLoading] = useState(false);

	const handleEdit = () => {
		setIsEditing(true);
		setEditedPrompt({ ...prompt });
	};

	const handleSave = async () => {
		onEdit(editedPrompt);
		setIsEditing(false);
	};

	const handleRemove = async () => {
		if (!onRemove) return;
		onRemove(editedPrompt);
		setIsEditing(false);
	};
	return (
		<Dialog open={isEditing} onOpenChange={setIsEditing}>
			<DialogTrigger asChild>
				{triggerButton ? (
					React.cloneElement(triggerButton as React.ReactElement, { onClick: handleEdit })
				) : (
					<Button variant="ghost" size="icon" onClick={handleEdit}>
						<Edit2Icon className="h-4 w-4" />
						<span className="sr-only">Edit Persona</span>
					</Button>
				)}
			</DialogTrigger>
			<DialogContent className="sm:max-w-[425px] md:max-w-[600px] lg:max-w-[700px] h-[80vh] flex flex-col">
				<DialogHeader>
					<DialogTitle className="text-xl">Edit Persona</DialogTitle>
					<DialogDescription>Modify your persona settings below.</DialogDescription>
				</DialogHeader>
				<div className="flex-1 overflow-y-auto px-1">
					<div className="space-y-6 py-4">
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label htmlFor="prompt">Search</Label>
								<Input
									id="prompt"
									type="text"
									value={editedPrompt.prompt || ''}
									onChange={(e) =>
										setEditedPrompt({ ...editedPrompt, prompt: e.target.value })
									}
									placeholder="Enter your search"
								/>
								<p className="text-sm text-gray-500">
									The main search query to find relevant companies
								</p>
							</div>
							<div className="space-y-2">
								<Label htmlFor="relevance">Relevance (Priority)</Label>
								<Input
									id="relevance"
									type="number"
									step="0.1"
									min="0"
									max="1"
									value={editedPrompt.relevance || 0.8}
									onChange={(e) =>
										setEditedPrompt({ ...editedPrompt, relevance: parseFloat(e.target.value) || 0.8 })
									}
									placeholder="0.8"
								/>
								<p className="text-sm text-gray-500">
									Priority level (0.0 - 1.0) for this persona
								</p>
							</div>
						</div>
						<div className="space-y-2">
							<Label htmlFor="rolePrompt">Role</Label>
							<Input
								id="rolePrompt"
								type="text"
								value={editedPrompt.rolePrompt || ''}
								onChange={(e) =>
									setEditedPrompt({ ...editedPrompt, rolePrompt: e.target.value })
								}
								placeholder="Enter the role prompt"
							/>
							<p className="text-sm text-gray-500">
								The specific role or position you're targeting (e.g., "CTO", "Marketing Director")
							</p>
						</div>
						<div className="space-y-2">
							<Label htmlFor="reason">Match Reason</Label>
							<Textarea
								id="reason"
								value={editedPrompt.reason || ''}
								onChange={(e) =>
									setEditedPrompt({ ...editedPrompt, reason: e.target.value })
								}
								placeholder="Enter your match reason"
								className="min-h-[100px]"
							/>
							<p className="text-sm text-gray-500">
								Explain why companies matching this persona are relevant to your outreach goals
							</p>
						</div>
						<div className="space-y-2">
							<Label htmlFor="examples">Example Companies</Label>
							<TagInput
								value={editedPrompt.examples || []}
								onChangeAction={(tags) => setEditedPrompt({ ...editedPrompt, examples: tags })}
								placeholder="Type company names and press Enter to add..."
								className="min-h-[80px]"
							/>
							<p className="text-sm text-gray-500">
								Add example companies that represent this persona. Type a company name and press Enter to add as a tag.
							</p>
						</div>
					</div>
				</div>
				<DialogFooter className="border-t pt-4">
					<div className="flex justify-between w-full">
						{onRemove && (
							<Button variant="destructive" onClick={handleRemove}>
								Remove
							</Button>
						)}
						<Button onClick={handleSave}>
							<Check className="mr-2 h-4 w-4" />
							Save changes
						</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
